import { computed, ref, watch } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';

import {
  type CadFile,
  DESIGN_ASSET_TYPES,
  QUOTING_ASSET_TYPES,
} from '@cutr/constants/order';
import { orderKeys, orderVariantKeys } from '@/queries/keys';
import {
  ANALYSIS_TIMEOUT_MS,
  orderFilesListOptions,
  useArchivedOrderFilesQuery,
  useInvalidateOrderFiles,
  useOrderFileMutations,
  useOrderFilesQuery,
  useReplacedRevisionsQuery,
  type InvalidateFilesOptions,
} from '@/queries/orderFiles';
import { useManufacturingIssueMutations } from '@/queries/manufacturingIssues';
import { asTimedOut, isRunning } from '@/helpers/cadfile-status';
import { useIndexById } from '@/composables/useIndexById';
import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';
import { CanceledError as AxiosCancelledError } from 'axios';
import {
  useQueryClient,
  CancelledError as TanstackCancelledError,
} from '@tanstack/vue-query';

export { type CadFilePatch } from '@/queries/orderFiles';

export const useOrderFiles = defineStore('orderFiles', () => {
  const queryClient = useQueryClient();
  const orderStore = useOrderStore();
  const userStore = useActiveUserStore();

  const activeFileId = ref('');
  const timedOutIds = ref(new Set<string>());
  const viewingRevision = ref<CadFile | null>(null);

  const activeVariantId = computed(() => orderStore.activeVariantId ?? '');

  const orderId = () => orderStore.currentOrder?.id;

  const {
    withPendingPatches,
    filesViewed,
    refreshFile,
    flushFileEdits,
    ...mutations
  } = useOrderFileMutations(orderId);

  const { setIssueVisibility, setIssueValidity } =
    useManufacturingIssueMutations();

  const {
    invalidateFiles: invalidateFilesForVariant,
    invalidateVariantData: invalidateVariantDataForVariant,
  } = useInvalidateOrderFiles(orderId);

  const invalidateFiles = (options?: InvalidateFilesOptions) =>
    invalidateFilesForVariant(activeVariantId.value, options);

  const invalidateVariantData = () =>
    invalidateVariantDataForVariant(activeVariantId.value);

  const { files: cachedFiles, query: listQuery } = useOrderFilesQuery(
    activeVariantId,
    { timedOutIds }
  );
  const fetching = computed(() => listQuery.isFetching.value);
  const isTimedOut = (fileId: string) => timedOutIds.value.has(fileId);

  // Analysis that never completes reads as failed after the timeout, without
  // writing a fake status into the cache.
  const files = computed<CadFile[]>(() => {
    const patched = withPendingPatches(cachedFiles.value);
    if (!timedOutIds.value.size) return patched;
    return patched.map((file) =>
      timedOutIds.value.has(file.id) ? asTimedOut(file) : file
    );
  });

  const analysisTimers = new Map<string, ReturnType<typeof setTimeout>>();

  function trackAnalysisTimeouts(runningIds: string[]) {
    analysisTimers.forEach((timer, id) => {
      if (runningIds.includes(id)) return;
      clearTimeout(timer);
      analysisTimers.delete(id);
      timedOutIds.value.delete(id);
    });
    runningIds.forEach((id) => {
      if (analysisTimers.has(id)) return;
      analysisTimers.set(
        id,
        setTimeout(() => timedOutIds.value.add(id), ANALYSIS_TIMEOUT_MS)
      );
    });
  }

  watch(
    () => cachedFiles.value.filter(isRunning).map((file) => file.id),
    trackAnalysisTimeouts,
    { immediate: true }
  );

  const canSeeAdminFiles = computed(
    () => userStore.isAdmin && userStore.isLoggedIn
  );
  const { archivedFiles: cachedArchivedFiles } = useArchivedOrderFilesQuery(
    activeVariantId,
    { enabled: canSeeAdminFiles }
  );
  const archivedFiles = computed(() =>
    canSeeAdminFiles.value ? cachedArchivedFiles.value : []
  );
  const { replacedRevisions: cachedReplacedRevisions, query: replacedQuery } =
    useReplacedRevisionsQuery(activeVariantId, { enabled: canSeeAdminFiles });
  const replacedRevisions = computed(() =>
    canSeeAdminFiles.value ? cachedReplacedRevisions.value : []
  );
  const replacedRevisionsLoading = computed(
    () => replacedQuery.isLoading.value
  );

  const filesForQuoting = computed<CadFile[]>(() =>
    files.value.filter(({ assetType }) =>
      QUOTING_ASSET_TYPES.includes(assetType)
    )
  );

  // Of the quoting-assetType files, those not explicitly descoped from
  // pricing/nesting via useForQuoting (which can diverge from assetType).
  const enabledFilesForQuoting = computed<CadFile[]>(() =>
    filesForQuoting.value.filter((file) => file.useForQuoting)
  );

  const filesWithIssues = computed<CadFile[]>(() =>
    filesForQuoting.value.map((file) => ({
      ...file,
      status: file.analysisStatus,
      issues: file.cadFileManufacturingIssues,
    }))
  );

  const allFiles = computed<CadFile[]>(() => [
    ...files.value,
    ...archivedFiles.value,
    ...replacedRevisions.value,
  ]);

  const getFileById = useIndexById(allFiles);

  const activeFile = computed<CadFile | undefined>(() =>
    getFileById(activeFileId.value)
  );

  // The PDF that has the active file listed in its quotingFiles, if any.
  const activeFileParent = computed<CadFile | undefined>(() =>
    files.value.find((f) =>
      f.quotingFiles?.some((qf) => qf.id === activeFileId.value)
    )
  );

  // When activeFile is a partlist, automatically resolve its reference PDF for the viewer.
  const partlistReferenceFile = computed<CadFile | undefined>(() =>
    activeFile.value?.filetype === 'partlist'
      ? activeFileParent.value
      : undefined
  );

  // When a quoting file (partlist, cabinet, 3D) is active, resolves to its parent PDF.
  // Otherwise the active file itself is the target.
  const quotingParentFile = computed<CadFile | undefined>(
    () => activeFileParent.value ?? activeFile.value
  );

  const selectedPartlistFileId = computed<string | null>(() =>
    activeFile.value?.filetype === 'partlist' ? activeFileId.value : null
  );

  // The active file when it is a quoting child (partlist, cabinet, 3D) of a PDF.
  const selectedQuotingFile = computed<CadFile | undefined>(() =>
    activeFileParent.value ? activeFile.value : undefined
  );

  const selectedQuotingFileId = computed<string | undefined>(
    () => selectedQuotingFile.value?.id
  );

  // viewingRevision is only set on the files tab.
  // partlistReferenceFile handles partlist → reference file resolution automatically.
  const viewingFile = computed<CadFile | undefined>(
    () =>
      viewingRevision.value ?? partlistReferenceFile.value ?? activeFile.value
  );

  const activeFileType = computed(() => {
    if (!activeFile.value) return null;
    return getAssetType(activeFile.value);
  });

  function getAssetType(file: CadFile) {
    const type = file.assetType;

    if (QUOTING_ASSET_TYPES.includes(type)) return 'quoting';
    if (DESIGN_ASSET_TYPES.includes(type)) return 'design';
    return 'production';
  }

  function setViewingRevision(revision: CadFile | null) {
    // Revisions come without their quoting files, so the current one is viewed through the store's own copy.
    const current = partlistReferenceFile.value ?? activeFile.value;
    viewingRevision.value = revision?.id === current?.id ? null : revision;
  }

  function clearViewingRevision() {
    viewingRevision.value = null;
  }

  function clearActiveFile() {
    activeFileId.value = '';
    clearViewingRevision();
  }

  function reset() {
    flushFileEdits();
    filesViewed.value = {};
    trackAnalysisTimeouts([]);
    queryClient.removeQueries({ queryKey: orderVariantKeys.root });
    queryClient.removeQueries({ queryKey: orderKeys.root });
  }

  /**
   * The list must be in the cache before the page resolves a file id from the URL.
   * De-duplicates with the observer's own fetch rather than forcing a second one.
   */
  async function ensureFiles(variantId: string) {
    if (!variantId) return;
    try {
      await queryClient.ensureQueryData(orderFilesListOptions(variantId));
    } catch (err) {
      if (
        err instanceof AxiosCancelledError ||
        err instanceof TanstackCancelledError
      )
        return;
      console.error(err);
    }
  }

  function getDefaultFileId(positionId?: string) {
    const defaultFile = positionId
      ? files.value.find(
          (file) =>
            file.positionId === positionId &&
            DESIGN_ASSET_TYPES.includes(file.assetType)
        )
      : filesForQuoting.value[0];

    return defaultFile?.id ?? '';
  }

  return {
    files,
    filesForQuoting,
    enabledFilesForQuoting,
    filesWithIssues,
    setIssueVisibility,
    setIssueValidity,
    fetching,
    isTimedOut,

    activeFileId,
    activeFile,
    activeFileParent,
    partlistReferenceFile,
    quotingParentFile,
    selectedQuotingFile,
    selectedQuotingFileId,
    viewingFile,
    viewingRevision,
    setViewingRevision,
    clearViewingRevision,
    clearActiveFile,
    activeFileType,
    getAssetType,

    reset,
    ensureFiles,
    invalidateFiles,
    invalidateVariantData,
    archivedFiles,
    replacedRevisions,
    replacedRevisionsLoading,
    allFiles,
    getDefaultFileId,
    getFileById,
    selectedPartlistFileId,

    ...mutations,
    refreshFile,
    flushFileEdits,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderFiles, import.meta.hot));
}
